import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useContext, useEffect, useState } from "react";
import { Bar, Pie } from "react-chartjs-2";
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    ChartData,
    ChartOptions,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from "chart.js";
import { APIContext } from "../../../../Contexts/APIContext";
import { SessionContext } from "../../../../Contexts/SessionContext";
import MonitoringReportDirectionOptionsDropdown from "../../../../Utilities/Constants/MonitoringReportDirectionOptionsDropdown";
import MonitoringReportResultsViewModel from "../../../ViewModels/TravelPlan/MonitoringReportResultsViewModel";
import ExportMonitoringReportViewModel from "../../../ViewModels/TravelPlan/ExportMonitoringReportViewModel";
import MonitoringReportExportDialog from "./MonitoringReportExportDialog";
import { getPieBackgroundColours, getPieBorderColours } from "../../../../Utilities/PieChartPalette";

ChartJS.register(ArcElement, Tooltip, Legend, Title, CategoryScale, LinearScale, BarElement);

export default function MonitoringReportSurveyResults() {
    const { post, postPDF, loading } = useContext(APIContext);
    const { sessionState } = useContext(SessionContext);

    const [monitoringReportResults, setMonitoringReportResults] = useState<MonitoringReportResultsViewModel[]>([]);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [isCalculatingResults, setIsCalculatingResults] = useState<boolean>(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

    useEffect(() => {
        if (sessionState.monitoringReport != null) {
            getData();
        }
    }, []);

    useEffect(() => {
        if (!loading) {
            setIsCalculatingResults(false);
            setIsGeneratingPdf(false);
        }
    }, [loading]);

    const getData = () => {
        setIsCalculatingResults(true);
        post("TravelPlan", "GetMonitoringReportResults", sessionState.monitoringReport, (results) => {
            setMonitoringReportResults(results);
            setIsCalculatingResults(false);
        });
    };

    const closeDialog = () => {
        setDialogOpen(false);
    };

    const openDialog = () => {
        setDialogOpen(true);
    };

    const getReport = (vm: ExportMonitoringReportViewModel) => {
        closeDialog();
        vm.monitoringReportResults = monitoringReportResults;
        vm.siteReference = sessionState.monitoringReport.siteReference;
        setIsGeneratingPdf(true);
        postPDF("PDFGenerator", "GetTravelPlanMonitoringReportPDF", vm.fileName ?? "Monitoring Report Export", vm.displayDirectlyToScreen, vm);
    };

    const buildPieData = (data: any[]): ChartData<"pie"> => ({
        labels: data.map((m) => {
            const percentage = Number(m.percentage ?? 0);
            return m.label
                ? `${m.label} ${isNaN(percentage) ? 0 : percentage.toFixed(1)}%`
                : "";
        }),
        datasets: [
            {
                label: "Percentage",
                data: data.map((d) => d.percentage),
                backgroundColor: getPieBackgroundColours(data),
                borderColor: getPieBorderColours(data),
                borderWidth: 1,
            },
        ],
    });

    const pieOptions = (title: string, data: any[]): ChartOptions<"pie"> => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true },
            title: {
                display: true,
                text: title,
                font: { size: 14, weight: "bold" },
                padding: 20,
            },
            tooltip: {
                displayColors: false,
                callbacks: {
                    title: () => "",
                    label: (context) => {
                        const item = data[context.dataIndex];
                        const val = Number(context.raw ?? 0);
                        return [
                            ...(item?.label ? [item.label] : []),
                            `Percentage: ${isNaN(val) ? 0 : val.toFixed(1)}%`,
                        ];
                    },
                },
            },
        },
    });

    const buildVehicleOccBarData = (vm: any): ChartData<"bar"> => ({
        labels: ["1", "2", "3", "4", "5", "6", "7"].map((v) => `${v}`),
        datasets: [
            {
                label: vm.yLabel ?? "Total Vehicles",
                data: [
                    vm.occupant1,
                    vm.occupant2,
                    vm.occupant3,
                    vm.occupant4,
                    vm.occupant5,
                    vm.occupant6,
                    vm.occupant7,
                ],
                backgroundColor: [
                    "rgba(255, 99, 132, 0.6)",
                    "rgba(54, 162, 235, 0.6)",
                    "rgba(255, 206, 86, 0.6)",
                    "rgba(75, 192, 192, 0.6)",
                    "rgba(153, 102, 255, 0.6)",
                    "rgba(255, 159, 64, 0.6)",
                    "rgba(100, 100, 100, 0.6)",
                ],
                borderColor: [
                    "rgba(255, 99, 132, 1)",
                    "rgba(54, 162, 235, 1)",
                    "rgba(255, 206, 86, 1)",
                    "rgba(75, 192, 192, 1)",
                    "rgba(153, 102, 255, 1)",
                    "rgba(255, 159, 64, 1)",
                    "rgba(100, 100, 100, 1)",
                ],
                borderWidth: 1,
            },
        ],
    });

    const vehicleOccBarOptions = (vm: any): ChartOptions<"bar"> => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true },
            title: {
                display: true,
                text: vm.title ?? "Occupancy Split Graph",
                font: { size: 14, weight: "bold" },
                padding: 20,
            },
        },
        scales: {
            x: {
                title: {
                    display: !!vm.xLabel,
                    text: vm.xLabel ?? "Occupants per vehicle",
                },
            },
            y: {
                beginAtZero: true,
                title: {
                    display: !!vm.yLabel,
                    text: vm.yLabel ?? "Total Vehicles",
                },
                ticks: {
                    precision: 0,
                },
            },
        },
    });

    const hasTripGeneration = (result: MonitoringReportResultsViewModel) =>
        !!(result.tripGeneration1?.results || result.tripGeneration2?.results || result.tripGeneration3?.results);

    const hasModeSplit = (split: any) => !!(split?.results && split.results.length > 0);

    const hasVehicleOccupancyData = (result: MonitoringReportResultsViewModel) =>
        !!result.vehicleOccupantsData && [
            result.vehicleOccupantsData.occupant1,
            result.vehicleOccupantsData.occupant2,
            result.vehicleOccupantsData.occupant3,
            result.vehicleOccupantsData.occupant4,
            result.vehicleOccupantsData.occupant5,
            result.vehicleOccupantsData.occupant6,
            result.vehicleOccupantsData.occupant7,
        ].some((value) => value != null);

    const getSiteLabel = (reference: string) =>
        reference === sessionState.monitoringReport.siteReference
            ? `${reference} (site selected)`
            : `${reference} (Earlier)`;

    const getDirectionLabel = (direction?: string | null) =>
        MonitoringReportDirectionOptionsDropdown.find((option) => option.id === direction)?.label ?? direction ?? "";

    const renderTripGeneration = (tripGeneration: MonitoringReportResultsViewModel["tripGeneration1"], title: string) => {
        if (!tripGeneration?.results) return null;

        return (
            <Stack spacing={0.5}>
                <Typography variant="subtitle1">{title}</Typography>
                <Typography variant="body2">{tripGeneration.mode}</Typography>
                <Typography variant="body2">{tripGeneration.selectedTimeRange}</Typography>
                <Typography variant="body2">{getDirectionLabel(tripGeneration.direction)}</Typography>
                <Typography variant="body2">{tripGeneration.results}</Typography>
            </Stack>
        );
    };

    const renderUserDefinedModeSplit = (split: any, title: string) => {
        if (!hasModeSplit(split)) return null;

        return (
            <Stack spacing={1}>
                <Typography variant="subtitle1">{title}</Typography>
                <Typography variant="body2">Time Range: {split.selectedTimeRange}</Typography>
                <Typography variant="body2">Direction: {getDirectionLabel(split.direction)}</Typography>
                <div style={{ width: "100%", height: 320 }}>
                    <Pie data={buildPieData(split.results)} options={pieOptions(title, split.results)} />
                </div>
            </Stack>
        );
    };

    return (
        <Grid container spacing={2}>
            <Grid sx={{ pt: 2 }} size={12}>
                <Button fullWidth={false} onClick={openDialog} disabled={isGeneratingPdf || isCalculatingResults}>
                    {isGeneratingPdf ? (
                        <>
                            <CircularProgress size={18} sx={{ mr: 1 }} />
                            Generating PDF...
                        </>
                    ) : (
                        "Generate Travel Plan Monitoring report PDF with Commentary"
                    )}
                </Button>
            </Grid>

            {isCalculatingResults && (
                <Grid size={12}>
                    <Paper sx={{ p: 4 }}>
                        <Stack spacing={2} alignItems="center" justifyContent="center">
                            <CircularProgress />
                            <Typography variant="h6">Calculating survey results...</Typography>
                            <Typography variant="body2">This may take a moment while we prepare the selected survey data.</Typography>
                        </Stack>
                    </Paper>
                </Grid>
            )}

            {!isCalculatingResults && monitoringReportResults.length === 0 && (
                <Grid size={12}>
                    <Paper sx={{ p: 4 }}>
                        <Typography variant="body2">No survey results are available for the selected surveys.</Typography>
                    </Paper>
                </Grid>
            )}

            {!isCalculatingResults && monitoringReportResults.map((result) => (
                <Grid key={`${result.reference}-${result.dateOfSurvey}`} size={6}>
                    <Paper sx={{ p: 3, height: "100%" }}>
                        <Stack spacing={2}>
                            <Stack spacing={0.5}>
                                <Typography variant="subtitle2">{getSiteLabel(result.reference)}</Typography>
                                <Typography variant="h5">{result.dateOfSurvey}</Typography>
                                <Typography variant="body2">{result.dayOfSurvey}</Typography>
                            </Stack>

                            {hasTripGeneration(result) && (
                                <>
                                    <Divider />
                                    <Stack spacing={2}>
                                        <Typography variant="h6">Trip Generation</Typography>
                                        {renderTripGeneration(result.tripGeneration1, "Trip Generation 1")}
                                        {renderTripGeneration(result.tripGeneration2, "Trip Generation 2")}
                                        {renderTripGeneration(result.tripGeneration3, "Trip Generation 3")}
                                    </Stack>
                                </>
                            )}

                            {(hasModeSplit(result.modeSplitTotal) || hasModeSplit(result.userDefinedModeSplit1) || hasModeSplit(result.userDefinedModeSplit2) || hasModeSplit(result.userDefinedModeSplit3)) && (
                                <>
                                        <Divider />
                                        <Stack spacing={3}>
                                            <Typography variant="h6">Mode Split Results</Typography>
                                            {renderUserDefinedModeSplit(result.modeSplitTotal, "Mode Split Total")}
                                            {renderUserDefinedModeSplit(result.userDefinedModeSplit1, "User Defined Mode Split 1")}
                                            {renderUserDefinedModeSplit(result.userDefinedModeSplit2, "User Defined Mode Split 2")}
                                            {renderUserDefinedModeSplit(result.userDefinedModeSplit3, "User Defined Mode Split 3")}
                                        </Stack>
                                </>
                            )}

                            {hasVehicleOccupancyData(result) && result.vehicleOccupantsData && (
                                <>
                                        <Divider />
                                        <Stack spacing={1}>
                                            <Typography variant="h6">Vehicle Occupancy Split Graph</Typography>
                                            <div style={{ width: "100%", height: 360 }}>
                                                <Bar
                                                    data={buildVehicleOccBarData(result.vehicleOccupantsData)}
                                                    options={vehicleOccBarOptions(result.vehicleOccupantsData)}
                                                />
                                            </div>
                                        </Stack>
                                </>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            ))}

            {dialogOpen && (
                <MonitoringReportExportDialog
                    handleClose={closeDialog}
                    dialogOpen={dialogOpen}
                    exportMonitoringReport={getReport}
                    isExporting={isGeneratingPdf}
                />
            )}
        </Grid>
    );
}
